/**
 * Mundalo - Magazine-grade renderer for the deep `magazine_data` blob
 * generated by `utils.magazine_data_generator`.
 *
 * This file is consumed by `PortalCityGuideDetail.js`. When `magazine_data`
 * is present on the API response, this layout takes over and renders all
 * 12 enriched sections (stats bar, overview, budget Frugal/Comfortable/
 * Upscale, safety with ✓/△/✗ icons, hospitals + procedure cost compare,
 * housing, QoL, social, internet provider table, moving checklists,
 * 12-dimension scorecard with rationales, 4-profile fit map).
 *
 * Falls back implicitly: the parent decides which renderer to use. If
 * `magazine_data` is missing, the parent uses the older section/full_guide
 * renderer instead - no layout regression for un-enriched cities.
 */
import React from 'react';
import {
  Users, Mountain, Wallet, Plane, Briefcase, ShieldCheck,
  Check, Triangle, X, Heart, Home as HomeIcon, Sun, Compass,
  Sparkles, Wifi, Truck, Star, AlertTriangle,
} from 'lucide-react';
import ViatorCtaCard from '../../components/ViatorCtaCard';

const cldTransform = (url, transform) => {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('/image/upload/')) return url;
  return url.replace(/\/image\/upload\/[^/]*\//, `/image/upload/${transform}/`);
};

const SectionDivider = ({ icon: Icon, label }) => (
  <div className="flex items-center justify-center gap-4 my-16" data-testid={`mag-divider-${label?.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <span className="block h-px flex-1 bg-[#0A1F2E]/15 max-w-[120px]" />
    <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#0A1F2E] text-[#E8A830]">
      <Icon className="w-5 h-5" />
    </span>
    <span className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#0A1F2E]/55">
      {label}
    </span>
    <span className="block h-px flex-1 bg-[#0A1F2E]/15 max-w-[120px]" />
  </div>
);

const Eyebrow = ({ children }) => (
  <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#E8A830] text-center mb-2">{children}</p>
);

const H2 = ({ children, className = '' }) => (
  <h2 className={`font-display italic text-4xl md:text-5xl text-[#0A1F2E] text-center leading-[1.05] mb-8 ${className}`}>{children}</h2>
);

// ============== STATS BAR ==============
const StatsBar = ({ stats }) => {
  if (!stats) return null;
  const items = [
    { k: 'Population',          v: stats.population,                     icon: Users },
    { k: 'Altitude',            v: stats.altitude,                       icon: Mountain },
    { k: 'Comfortable budget',  v: stats.comfortable_monthly_budget_usd ? `$${stats.comfortable_monthly_budget_usd.toLocaleString()}/mo` : null, icon: Wallet },
    { k: 'Americans here',      v: stats.americans_living_here,          icon: Plane },
    { k: 'Coworking spaces',    v: stats.coworking_spaces ? `${stats.coworking_spaces}+` : null, icon: Briefcase },
    { k: 'US advisory',         v: stats.us_state_dept_advisory,         icon: ShieldCheck },
  ].filter((i) => i.v);
  return (
    <section className="bg-[#F4ECD8] border-y border-[#EFE6CF]" data-testid="mag-stats-bar">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.k} className="text-center md:text-left">
                <Icon className="w-4 h-4 text-[#E8A830] mb-1.5 inline-block md:block" />
                <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E]">{it.k}</p>
                <p className="font-display text-lg text-[#0A1F2E] leading-tight mt-0.5">{it.v}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============== OVERVIEW ==============
const Overview = ({ paragraphs, heroUrl }) => {
  if (!paragraphs?.length) return null;
  return (
    <section className="max-w-3xl mx-auto px-4 md:px-8 py-16" data-testid="mag-overview">
      <Eyebrow>The City</Eyebrow>
      <H2>An honest portrait</H2>
      <div className="space-y-6">
        {paragraphs.map((p, i) => (
          <p
            key={i}
            className={i === 0
              ? 'text-xl md:text-2xl text-[#0A1F2E]/90 leading-relaxed first-letter:font-display first-letter:italic first-letter:text-5xl first-letter:font-semibold first-letter:text-[#1A5C6E] first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-none'
              : 'text-base md:text-lg text-[#0A1F2E]/80 leading-relaxed'
            }
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', lineHeight: 1.7 }}
          >
            {p}
          </p>
        ))}
      </div>
    </section>
  );
};

// ============== BUDGET ==============
const BudgetSection = ({ budget, heroUrl }) => {
  if (!budget?.table?.length) return null;
  const sectionImg = heroUrl ? cldTransform(heroUrl, 'w_2000,h_900,c_fill,g_auto:subject,e_brightness:6,q_auto,f_auto') : null;
  return (
    <section className="max-w-4xl mx-auto px-4 md:px-8" data-testid="mag-budget">
      <SectionDivider icon={Wallet} label="Chapter 01 · Budget" />
      <Eyebrow>Money matters</Eyebrow>
      <H2>What it actually costs</H2>
      {sectionImg && (
        <div className="w-full aspect-[16/9] rounded-lg bg-cover bg-center my-10" style={{ backgroundImage: `url(${sectionImg})` }} aria-hidden="true" />
      )}
      <div className="overflow-x-auto bg-white border border-[#EFE6CF] rounded-2xl">
        <table className="w-full text-sm">
          <thead className="bg-[#F4ECD8]">
            <tr>
              <th className="text-left px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Item</th>
              <th className="text-right px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Frugal</th>
              <th className="text-right px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Comfortable</th>
              <th className="text-right px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Upscale</th>
            </tr>
          </thead>
          <tbody>
            {budget.table.map((row, i) => {
              const isTotal = (row.item || '').toUpperCase().includes('TOTAL');
              return (
                <tr key={i} className={`border-t border-[#EFE6CF] ${isTotal ? 'bg-[#F9F6EF] font-semibold' : ''}`}>
                  <td className="px-5 py-3 text-[#0A1F2E]">{row.item}</td>
                  <td className="px-5 py-3 text-right text-[#0A1F2E]/80 font-mono">${(row.frugal_usd || 0).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</td>
                  <td className="px-5 py-3 text-right text-[#0A1F2E] font-mono">${(row.comfortable_usd || 0).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</td>
                  <td className="px-5 py-3 text-right text-[#0A1F2E]/80 font-mono">${(row.upscale_usd || 0).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {budget.financial_tips?.length > 0 && (
        <div className="mt-10">
          <p className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#E8A830] mb-3">5 financial tips for this city</p>
          <ul className="space-y-3">
            {budget.financial_tips.map((tip, i) => (
              <li key={i} className="flex gap-3 text-[#0A1F2E]/85" data-testid={`mag-tip-${i}`}>
                <span className="font-display text-2xl italic text-[#E8A830] leading-none mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                <span className="leading-relaxed text-base">{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

// ============== SAFETY ==============
const SafetyIcon = ({ rating }) => {
  const r = (rating || '').toLowerCase();
  if (r === '✓' || r === 'check' || r === 'safe' || r === 'good')
    return <span className="inline-flex w-7 h-7 items-center justify-center rounded-full bg-[#2CA8A0]/15 text-[#1A5C6E]"><Check className="w-4 h-4" /></span>;
  if (r === '△' || r === 'warn' || r === 'caution' || r === 'mixed')
    return <span className="inline-flex w-7 h-7 items-center justify-center rounded-full bg-[#E8A830]/20 text-[#8A6310]"><Triangle className="w-3.5 h-3.5" /></span>;
  if (r === '✗' || r === 'x' || r === 'unsafe' || r === 'bad')
    return <span className="inline-flex w-7 h-7 items-center justify-center rounded-full bg-[#B91C1C]/15 text-[#7F1414]"><X className="w-4 h-4" /></span>;
  return <span className="text-[#0A1F2E]/40">-</span>;
};

const SafetySection = ({ safety, heroUrl }) => {
  if (!safety) return null;
  const sectionImg = heroUrl ? cldTransform(heroUrl, 'w_2000,h_900,c_fill,g_north,e_sharpen:80,q_auto,f_auto') : null;
  return (
    <section className="max-w-4xl mx-auto px-4 md:px-8" data-testid="mag-safety">
      <SectionDivider icon={ShieldCheck} label="Chapter 02 · Safety" />
      <Eyebrow>The reality on the ground</Eyebrow>
      <H2>Safety, neighborhood by neighborhood</H2>
      {sectionImg && (
        <div className="w-full aspect-[16/9] rounded-lg bg-cover bg-center my-10" style={{ backgroundImage: `url(${sectionImg})` }} aria-hidden="true" />
      )}
      {safety.neighborhood_table?.length > 0 && (
        <div className="overflow-x-auto bg-white border border-[#EFE6CF] rounded-2xl">
          <table className="w-full text-sm">
            <thead className="bg-[#F4ECD8]">
              <tr>
                <th className="text-left px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Neighborhood</th>
                <th className="text-center px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Rating</th>
                <th className="text-left px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Notes for expats</th>
              </tr>
            </thead>
            <tbody>
              {safety.neighborhood_table.map((row, i) => (
                <tr key={i} className="border-t border-[#EFE6CF]">
                  <td className="px-5 py-3 text-[#0A1F2E] font-display italic text-lg">{row.neighborhood}</td>
                  <td className="px-5 py-3 text-center"><SafetyIcon rating={row.rating} /></td>
                  <td className="px-5 py-3 text-[#0A1F2E]/80">{row.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {safety.safety_habits?.length > 0 && (
        <div className="mt-10">
          <p className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#E8A830] mb-3">Safety habits worth building</p>
          <ul className="space-y-3">
            {safety.safety_habits.map((h, i) => (
              <li key={i} className="flex gap-3 text-[#0A1F2E]/85" data-testid={`mag-habit-${i}`}>
                <span className="font-display text-2xl italic text-[#E8A830] leading-none mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                <span className="leading-relaxed text-base">{h}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

// ============== HEALTHCARE ==============
const HealthcareSection = ({ healthcare, heroUrl }) => {
  if (!healthcare) return null;
  const sectionImg = heroUrl ? cldTransform(heroUrl, 'w_2000,h_900,c_fill,g_south,e_saturation:-30,q_auto,f_auto') : null;
  return (
    <section className="max-w-4xl mx-auto px-4 md:px-8" data-testid="mag-healthcare">
      <SectionDivider icon={Heart} label="Chapter 03 · Healthcare" />
      <Eyebrow>Hospitals, savings, insurance</Eyebrow>
      <H2>What care looks like here</H2>
      {sectionImg && (
        <div className="w-full aspect-[16/9] rounded-lg bg-cover bg-center my-10" style={{ backgroundImage: `url(${sectionImg})` }} aria-hidden="true" />
      )}

      {healthcare.hospitals?.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          {healthcare.hospitals.map((h, i) => (
            <div key={i} className="bg-white border border-[#EFE6CF] rounded-xl p-5">
              <h4 className="font-display italic text-xl text-[#0A1F2E] mb-1">{h.name}</h4>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#1A5C6E] font-mono mb-2">{h.accreditation}</p>
              <p className="text-sm text-[#0A1F2E]/80 leading-relaxed">{h.notes}</p>
            </div>
          ))}
        </div>
      )}

      {healthcare.procedure_costs?.length > 0 && (
        <>
          <p className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#E8A830] mb-3 text-center">US vs local - what you save</p>
          <div className="overflow-x-auto bg-white border border-[#EFE6CF] rounded-2xl mb-10">
            <table className="w-full text-sm">
              <thead className="bg-[#F4ECD8]">
                <tr>
                  <th className="text-left px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Procedure</th>
                  <th className="text-right px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">US cost</th>
                  <th className="text-right px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Local cost</th>
                  <th className="text-right px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Savings</th>
                </tr>
              </thead>
              <tbody>
                {healthcare.procedure_costs.map((p, i) => (
                  <tr key={i} className="border-t border-[#EFE6CF]">
                    <td className="px-5 py-3 text-[#0A1F2E]">{p.procedure}</td>
                    <td className="px-5 py-3 text-right font-mono text-[#0A1F2E]/65 line-through">${(p.us_usd || 0).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</td>
                    <td className="px-5 py-3 text-right font-mono text-[#0A1F2E] font-semibold">${(p.local_usd || 0).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</td>
                    <td className="px-5 py-3 text-right font-mono text-[#1A5C6E] font-semibold">{p.savings_pct ? `${p.savings_pct}%` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {healthcare.insurance_options?.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-4">
          {healthcare.insurance_options.map((opt, i) => (
            <div key={i} className="bg-[#F4ECD8] border border-[#EFE6CF] rounded-xl p-5">
              <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-1">Insurance · option {i + 1}</p>
              <p className="font-display italic text-xl text-[#0A1F2E] leading-tight mb-1">{opt.type}</p>
              {opt.cost && <p className="text-sm font-mono text-[#E8A830] mb-2">{opt.cost}</p>}
              <p className="text-sm text-[#0A1F2E]/80 leading-relaxed">{opt.notes}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ============== HOUSING ==============
const HousingSection = ({ housing, heroUrl }) => {
  if (!housing) return null;
  const sectionImg = heroUrl ? cldTransform(heroUrl, 'w_2000,h_900,c_fill,g_west,q_auto,f_auto') : null;
  return (
    <section className="max-w-4xl mx-auto px-4 md:px-8" data-testid="mag-housing">
      <SectionDivider icon={HomeIcon} label="Chapter 04 · Housing" />
      <Eyebrow>Where to land</Eyebrow>
      <H2>The neighborhood map</H2>
      {sectionImg && (
        <div className="w-full aspect-[16/9] rounded-lg bg-cover bg-center my-10" style={{ backgroundImage: `url(${sectionImg})` }} aria-hidden="true" />
      )}
      {housing.neighborhoods?.length > 0 && (
        <div className="overflow-x-auto bg-white border border-[#EFE6CF] rounded-2xl mb-8">
          <table className="w-full text-sm">
            <thead className="bg-[#F4ECD8]">
              <tr>
                <th className="text-left px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Neighborhood</th>
                <th className="text-left px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Best for</th>
                <th className="text-right px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">1BR rent</th>
                <th className="text-left px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Notes</th>
              </tr>
            </thead>
            <tbody>
              {housing.neighborhoods.map((n, i) => (
                <tr key={i} className="border-t border-[#EFE6CF]">
                  <td className="px-5 py-3 font-display italic text-lg text-[#0A1F2E]">{n.name}</td>
                  <td className="px-5 py-3 text-[#0A1F2E]/75 text-xs font-mono uppercase tracking-[0.12em]">{n.best_for}</td>
                  <td className="px-5 py-3 text-right font-mono text-[#1A5C6E] font-semibold">{n.one_br_usd ? `$${n.one_br_usd.toLocaleString('en-US', { timeZone: 'America/Chicago' })}` : '-'}</td>
                  <td className="px-5 py-3 text-[#0A1F2E]/80">{n.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {housing.rental_process_tips?.length > 0 && (
          <div>
            <p className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#E8A830] mb-3">Rental process tips</p>
            <ul className="space-y-2">
              {housing.rental_process_tips.map((t, i) => (
                <li key={i} className="flex gap-2 text-[#0A1F2E]/85 text-sm leading-relaxed">
                  <span className="text-[#E8A830] mt-1">•</span><span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {housing.where_to_search?.length > 0 && (
          <div>
            <p className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#E8A830] mb-3">Where to search</p>
            <ul className="space-y-2">
              {housing.where_to_search.map((t, i) => (
                <li key={i} className="flex gap-2 text-[#0A1F2E]/85 text-sm leading-relaxed">
                  <span className="text-[#E8A830] mt-1">•</span><span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

// ============== QOL ==============
const QolSection = ({ qol }) => {
  if (!qol) return null;
  const items = [
    { label: 'Climate',          v: qol.climate,            icon: Sun },
    { label: 'Getting around',   v: qol.getting_around,     icon: Compass },
    { label: 'Culture & rec',    v: qol.culture_recreation, icon: Sparkles },
  ].filter((i) => i.v);
  if (!items.length) return null;
  return (
    <section className="max-w-4xl mx-auto px-4 md:px-8" data-testid="mag-qol">
      <SectionDivider icon={Sparkles} label="Chapter 05 · Quality of Life" />
      <Eyebrow>The everyday</Eyebrow>
      <H2>What life feels like</H2>
      <div className="grid md:grid-cols-3 gap-5">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <div key={it.label} className="bg-white border border-[#EFE6CF] rounded-2xl p-6">
              <Icon className="w-5 h-5 text-[#E8A830] mb-2" />
              <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-2">{it.label}</p>
              <p className="text-sm text-[#0A1F2E]/85 leading-relaxed">{it.v}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ============== SOCIAL ==============
const SocialSection = ({ social }) => {
  if (!social) return null;
  return (
    <section className="max-w-4xl mx-auto px-4 md:px-8" data-testid="mag-social">
      <SectionDivider icon={Users} label="Chapter 06 · Community" />
      <Eyebrow>Finding your people</Eyebrow>
      <H2>The expat scene</H2>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-white border border-[#EFE6CF] rounded-2xl p-6">
          <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-2">American community size</p>
          <p className="font-display italic text-2xl text-[#0A1F2E] mb-3">{social.community_size}</p>
          {social.where_americans_gather && (
            <>
              <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mt-4 mb-1">Where they gather</p>
              <p className="text-sm text-[#0A1F2E]/85 leading-relaxed">{social.where_americans_gather}</p>
            </>
          )}
        </div>
        <div className="bg-[#F4ECD8] border border-[#EFE6CF] rounded-2xl p-6">
          {social.facebook_groups?.length > 0 && (
            <>
              <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-2">Facebook groups worth joining</p>
              <ul className="space-y-1.5 mb-4">
                {social.facebook_groups.map((g, i) => (
                  <li key={i} className="text-sm text-[#0A1F2E]/85 font-display italic">- {g}</li>
                ))}
              </ul>
            </>
          )}
          {social.language_notes && (
            <>
              <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-1">Language notes</p>
              <p className="text-sm text-[#0A1F2E]/85 leading-relaxed">{social.language_notes}</p>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

// ============== INTERNET ==============
const InternetSection = ({ internet }) => {
  if (!internet) return null;
  return (
    <section className="max-w-4xl mx-auto px-4 md:px-8" data-testid="mag-internet">
      <SectionDivider icon={Wifi} label="Chapter 07 · Connectivity" />
      <Eyebrow>Wifi and SIMs</Eyebrow>
      <H2>Staying online</H2>
      {internet.providers?.length > 0 && (
        <div className="overflow-x-auto bg-white border border-[#EFE6CF] rounded-2xl mb-6">
          <table className="w-full text-sm">
            <thead className="bg-[#F4ECD8]">
              <tr>
                <th className="text-left px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Provider</th>
                <th className="text-left px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Speed</th>
                <th className="text-right px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Monthly</th>
                <th className="text-left px-5 py-4 font-mono uppercase text-[10px] tracking-widest text-[#1A5C6E]">Notes</th>
              </tr>
            </thead>
            <tbody>
              {internet.providers.map((p, i) => (
                <tr key={i} className="border-t border-[#EFE6CF]">
                  <td className="px-5 py-3 font-display italic text-lg text-[#0A1F2E]">{p.provider}</td>
                  <td className="px-5 py-3 text-[#0A1F2E]/80 font-mono text-xs">{p.speed_range}</td>
                  <td className="px-5 py-3 text-right font-mono text-[#1A5C6E] font-semibold">{p.monthly_usd ? `$${p.monthly_usd}` : '-'}</td>
                  <td className="px-5 py-3 text-[#0A1F2E]/80">{p.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {internet.mobile_carrier_recommendations?.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-4">
          {internet.mobile_carrier_recommendations.map((c, i) => (
            <div key={i} className="bg-[#F4ECD8] border border-[#EFE6CF] rounded-xl p-5">
              <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-1">Mobile · option {i + 1}</p>
              <p className="font-display italic text-xl text-[#0A1F2E] mb-1">{c.carrier}</p>
              <p className="text-sm text-[#0A1F2E]/80 leading-relaxed">{c.why}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ============== MOVING ==============
const MovingSection = ({ moving }) => {
  if (!moving) return null;
  return (
    <section className="max-w-4xl mx-auto px-4 md:px-8" data-testid="mag-moving">
      <SectionDivider icon={Truck} label="Chapter 08 · Moving" />
      <Eyebrow>Logistics</Eyebrow>
      <H2>Before & after you arrive</H2>
      <div className="grid md:grid-cols-2 gap-5">
        {moving.before_arrive?.length > 0 && (
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-6">
            <p className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#E8A830] mb-3">Before you arrive</p>
            <ul className="space-y-2">
              {moving.before_arrive.map((t, i) => (
                <li key={i} className="flex gap-3 text-[#0A1F2E]/85 text-sm leading-relaxed">
                  <Check className="w-4 h-4 text-[#2CA8A0] mt-0.5 shrink-0" /><span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {moving.after_arrive?.length > 0 && (
          <div className="bg-[#F4ECD8] border border-[#EFE6CF] rounded-2xl p-6">
            <p className="font-mono uppercase tracking-[0.22em] text-[10px] text-[#E8A830] mb-3">After you arrive</p>
            <ul className="space-y-2">
              {moving.after_arrive.map((t, i) => (
                <li key={i} className="flex gap-3 text-[#0A1F2E]/85 text-sm leading-relaxed">
                  <Check className="w-4 h-4 text-[#2CA8A0] mt-0.5 shrink-0" /><span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

// ============== SCORECARD ==============
const Scorecard = ({ scorecard }) => {
  if (!scorecard) return null;
  const dims = [
    ['affordability', 'Affordability'],
    ['safety', 'Safety'],
    ['healthcare', 'Healthcare'],
    ['infrastructure', 'Infrastructure'],
    ['language_ease', 'Language ease'],
    ['expat_community', 'Expat community'],
    ['quality_of_life', 'Quality of life'],
    ['connectivity', 'Connectivity'],
    ['education_families', 'Education (families)'],
    ['employment', 'Employment'],
    ['moving_logistics', 'Moving logistics'],
    ['food_daily_life', 'Food & daily life'],
  ];
  const overall = scorecard.overall_weighted_1_10 || {};
  return (
    <section className="bg-[#0A1F2E] text-white py-20" data-testid="mag-scorecard">
      <div className="max-w-5xl mx-auto px-4 md:px-8">
        <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#E8A830] text-center mb-2">Editorial scorecard</p>
        <H2 className="!text-white">12 dimensions, honestly rated</H2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 mb-14">
          {dims.map(([k, label]) => {
            const dim = scorecard[k];
            if (!dim) return null;
            return (
              <div key={k} className="border-t border-white/15 pt-3" data-testid={`mag-score-${k}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-white/55">{label}</p>
                  <p className="font-display text-3xl text-white leading-none">{dim.score_1_10}<span className="text-xs text-white/35 ml-1">/10</span></p>
                </div>
                <p className="text-xs text-white/70 leading-relaxed">{dim.why}</p>
              </div>
            );
          })}
        </div>

        {overall.score ? (
          <div className="text-center border-t border-white/20 pt-10" data-testid="mag-score-overall">
            <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#E8A830] mb-2">Overall weighted score</p>
            <p className="font-display text-7xl md:text-8xl text-white leading-none">
              {overall.score}<span className="text-3xl text-white/40 ml-2">/10</span>
            </p>
            <p className="mt-4 max-w-xl mx-auto text-white/75 italic" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
              "{overall.summary}"
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
};

// ============== WHO SHOULD MOVE ==============
const WhoShouldMove = ({ profiles }) => {
  if (!profiles?.length) return null;
  return (
    <section className="max-w-5xl mx-auto px-4 md:px-8 py-20" data-testid="mag-profiles">
      <p className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#E8A830] text-center mb-2">Reader match</p>
      <H2>Who should move here</H2>
      <div className="grid md:grid-cols-2 gap-5">
        {profiles.map((p, i) => (
          <article key={i} className="bg-white border border-[#EFE6CF] rounded-2xl p-7" data-testid={`mag-profile-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <Star className="w-4 h-4 text-[#E8A830]" />
              <span className="text-xs font-mono uppercase tracking-[0.18em] text-[#1A5C6E]">{p.fit_1_10}/10 fit</span>
            </div>
            <h3 className="font-display italic text-3xl text-[#0A1F2E] mb-4 leading-tight">{p.profile}</h3>
            {p.why_works?.length > 0 && (
              <>
                <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#2CA8A0] mb-2">Why it works</p>
                <ul className="space-y-1.5 mb-4">
                  {p.why_works.map((w, j) => (
                    <li key={j} className="flex gap-2 text-sm text-[#0A1F2E]/85 leading-relaxed">
                      <Check className="w-3.5 h-3.5 text-[#2CA8A0] mt-1 shrink-0" /><span>{w}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {p.watchouts?.length > 0 && (
              <>
                <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#B91C1C] mb-2">Watch-outs</p>
                <ul className="space-y-1.5">
                  {p.watchouts.map((w, j) => (
                    <li key={j} className="flex gap-2 text-sm text-[#0A1F2E]/85 leading-relaxed">
                      <AlertTriangle className="w-3.5 h-3.5 text-[#B91C1C] mt-1 shrink-0" /><span>{w}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </article>
        ))}
      </div>
    </section>
  );
};


// ============== ROOT ==============
const MagazineLayout = ({ data }) => {
  const md = data.magazine_data;
  if (!md) return null;
  const { hero_image_url } = data;
  const cityName = data.name || 'this city';
  return (
    <>
      <StatsBar stats={md.stats_bar} />
      <Overview paragraphs={md.overview_paragraphs} heroUrl={hero_image_url} />
      <BudgetSection budget={md.budget} heroUrl={hero_image_url} />
      <SafetySection safety={md.safety} heroUrl={hero_image_url} />
      <HealthcareSection healthcare={md.healthcare} heroUrl={hero_image_url} />
      <HousingSection housing={md.housing} heroUrl={hero_image_url} />
      <QolSection qol={md.quality_of_life} />

      {/* Viator affiliate - placed directly after Quality of Life on every
          enriched city guide (iter 77). Visible to all tiers, never paywalled. */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 my-12">
        <ViatorCtaCard
          eyebrow="Experiences & Tours"
          headline={`Explore ${cityName} like a local`}
          subtext="Handpicked tours and experiences for new arrivals."
          testId="viator-cta-city-guide"
          surface="city_guide"
          cityName={cityName}
        />
      </section>

      <SocialSection social={md.social_life} />
      <InternetSection internet={md.internet} />
      <MovingSection moving={md.moving} />
      <Scorecard scorecard={md.scorecard} />
      <WhoShouldMove profiles={md.who_should_move} />
    </>
  );
};

export default MagazineLayout;
